import type { Ref } from "react";
import "./text-field.css";
import "./name-fields.css";

// Nome e cognome di una persona, sempre in due caselle (decisione del product owner del 30 settembre 2026): una
// accanto all'altra sullo schermo largo, una sotto l'altra sotto i 720 px, con un solo errore per tutte e due,
// come il dialog «Invita una persona» del mock-up del Team. Il limite di 60 caratteri è quello dei contratti.

export type NamePart = "firstName" | "lastName";

type PartProps = {
  label: string;
  value: string;
  /** Assente soltanto in sola lettura. */
  onChange?: (value: string) => void;
  /** Arriva all'`<input>`, per spostare il focus sul campo sbagliato. */
  ref?: Ref<HTMLInputElement>;
};

type Props = {
  /** Prefisso degli id: `<id>-first`, `<id>-last`, `<id>-hint`, `<id>-error`. */
  id: string;
  firstName: PartProps;
  lastName: PartProps;
  /** Una spiegazione sotto le due caselle, legata a tutte e due. */
  hint?: string;
  /** L'errore condiviso, scritto una volta sola. */
  error?: string;
  /** La casella a cui l'errore si riferisce (di solito quella che manca); senza, tutte e due. */
  invalid?: NamePart | "both";
  readOnly?: boolean;
  /** Per chi usa uno screen reader: tutte e due vanno scritte (il controllo resta del modulo, che è `noValidate`). */
  required?: boolean;
  /** `person`: il nome di chi compila, che il browser può proporre; `off`: il nome di un'altra persona. */
  autoComplete?: "person" | "off";
};

/** Il limite di ciascuna parte (`personNamePartSchema` dei contratti). */
const PART_MAX_LENGTH = 60;

/** La prima casella vuota nell'ordine del modulo (gli spazi non contano), oppure `null` se ci sono tutte e due. */
export function missingNamePart(firstName: string, lastName: string): NamePart | null {
  if (!firstName.trim()) return "firstName";
  if (!lastName.trim()) return "lastName";
  return null;
}

export function NameFields({ id, firstName, lastName, hint, error, invalid = "both", readOnly, required, autoComplete = "off" }: Props) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  function field(part: NamePart, spec: PartProps) {
    const inputId = `${id}-${part === "firstName" ? "first" : "last"}`;
    // L'errore si legge soltanto sulla casella sbagliata: quella giusta non è «invalida».
    const wrong = Boolean(error) && (invalid === "both" || invalid === part);
    const describedBy = [hint ? hintId : null, wrong ? errorId : null].filter(Boolean).join(" ");
    const personal = part === "firstName" ? "given-name" : "family-name";
    return (
      <div className="g-field">
        <label className="g-field__label" htmlFor={inputId}>{spec.label}</label>
        <input
          id={inputId}
          ref={spec.ref}
          className="g-field__input"
          type="text"
          value={spec.value}
          maxLength={PART_MAX_LENGTH}
          readOnly={readOnly}
          required={required}
          autoComplete={autoComplete === "person" && !readOnly ? personal : "off"}
          aria-invalid={wrong ? "true" : undefined}
          aria-describedby={describedBy || undefined}
          onChange={(event) => spec.onChange?.(event.target.value)}
        />
      </div>
    );
  }

  return (
    <div className="g-name-fields">
      <div className="g-name-fields__pair">
        {field("firstName", firstName)}
        {field("lastName", lastName)}
      </div>
      {hint ? <p id={hintId} className="g-field__hint">{hint}</p> : null}
      {error ? <p id={errorId} className="g-field__error" role="alert">{error}</p> : null}
    </div>
  );
}
